import {
  IconColor,
  type IconVariant,
  MolsonBox,
  MolsonDivider,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import {
  type BoxProps,
  DialogTitle,
  type SxProps,
  styled,
  type Theme
} from '@mui/material'

type MolsonDialogHeaderProps = BoxProps & {
  dataTestId?: string
  title: string
  subTitle?: string
  hasCloseIcon?: boolean
  headerIcon?: IconVariant | null
  iconColor?: IconColor
  handleCloseDialog?: () => void
  sx?: SxProps<Theme>
  titleSx?: SxProps<Theme>
  subTitleSx?: SxProps<Theme>
  headerDivider?: boolean
  postDividerText?: string
  postDividerTextSx?: SxProps<Theme>
  displayDivider?: boolean
}

const HeaderContainer = styled(MolsonBox)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
})

const IconTitleContainer = styled(MolsonBox)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
})

export const MolsonDialogHeader = ({
  dataTestId,
  title,
  subTitle,
  hasCloseIcon = false,
  headerIcon,
  iconColor,
  handleCloseDialog,
  sx,
  titleSx,
  subTitleSx,
  headerDivider,
  postDividerText,
  postDividerTextSx,
  displayDivider = true,
  ...props
}: MolsonDialogHeaderProps) => {
  const defaultSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5
  }

  const titleBlock = headerIcon ? (
    <IconTitleContainer>
      <MolsonIcon variant={headerIcon} iconColor={iconColor} />
      <DialogTitle sx={titleSx}>{title}</DialogTitle>
    </IconTitleContainer>
  ) : (
    <MolsonBox display="flex" gap={2} alignItems="center">
      <DialogTitle sx={titleSx}>{title}</DialogTitle>
      {headerDivider && postDividerText ? (
        <>
          <MolsonDivider orientation="vertical" sx={{ width: 5, height: 43 }} />
          <MolsonTypography sx={postDividerTextSx}>
            {postDividerText}
          </MolsonTypography>
        </>
      ) : null}
    </MolsonBox>
  )

  return (
    <MolsonBox sx={{ ...defaultSx, ...sx }} data-testid={dataTestId} {...props}>
      {hasCloseIcon ? (
        <HeaderContainer>
          {titleBlock}
          <MolsonIcon
            variant="CLOSE_X"
            cursor="pointer"
            onClick={handleCloseDialog}
          />
        </HeaderContainer>
      ) : (
        titleBlock
      )}

      {subTitle && (
        <MolsonTypography variant="subtitle1" sx={subTitleSx}>
          {subTitle}
        </MolsonTypography>
      )}

      {displayDivider ? (
        <MolsonDivider orientation="horizontal" sx={{ mt: 0.5 }} />
      ) : null}
    </MolsonBox>
  )
}
